<template>
  <div class="table-section">
    <div class="table-top">
      <b-row>
        <b-col md="4" lg="4" class="my-1">
          <p class="table-filter text-left">
            <BaseInput
              type="text"
              label="Search"
              :inputType="'text'"
              v-model="filter"
              placeholder="Search something..."
              :hasPreIcon="false"
            >
            </BaseInput>
          </p>
        </b-col>
        <b-col md="8" lg="8" class="my-1">
          <p class="table-action text-right">
            <span class="excel-download-btn-hold">
              <button
                type="button"
                class="btn btn-primary btn-semi-rounded mb-2 mr-2"
              >
                <i class="bx bxs-report"></i> &nbsp; &nbsp;
                <download-excel
                  class="excel-download-btn"
                  :data="items"
                  :fields="json_fields"
                  worksheet="My Worksheet"
                  name="filename.xls"
                >
                  Download As Excel
                </download-excel>
              </button>
            </span>
          </p>
        </b-col>
      </b-row>
    </div>

    <b-table
      striped
      hover
      :fields="fields"
      :items="items"
      :busy="isBusy"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      sort-icon-left
      responsive="sm"
      :current-page="currentPage"
      :per-page="perPage"
      :filter="filter"
      :filterIncludedFields="filterOn"
      :sort-direction="sortDirection"
      @filtered="onFiltered"
    >
      <!-- A virtual column -->
      <template v-slot:cell(index)="data">
        {{ data.index + 1 }}
      </template>

      <template v-slot:cell(privilegeId)="data">
        <p class="name-field">
          <span>{{ data.item.privilegeId }}</span>
        </p>
      </template>

      <!-- A virtual composite column -->
      <template v-slot:cell(privilegeModuleName)="data">
        <p class="name-field">
          <span>{{ data.item.privilegeModuleName }}</span>
        </p>
      </template>

      <!-- A custom formatted column -->
      <template v-slot:cell(privilegeType)="data">
        <p class="name-field">
          <span>{{ data.item.privilegeType }}</span>
        </p>
      </template>

      <!-- A virtual composite column -->
      <template v-slot:cell(privilegeMaker)="data">
        <p class="name-field">
          <span>{{ data.item.privilegeMaker }}</span>
        </p>
      </template>

      <!-- A virtual composite column -->
      <template v-slot:cell(privilegeChecker)="data">
        <p class="name-field">
          <span>{{ data.item.privilegeChecker }}</span>
        </p>
      </template>

      <!-- A virtual composite column -->
      <template v-slot:cell(privilegeStatus)="data">
        <p class="name-field">
          <span
            v-if="data.item.privilegeStatus === 'ACTIVE'"
            class="badge badge-pill badge-soft-success font-size-12"
            >{{ data.item.privilegeStatus }}</span
          >
          <span
            v-if="data.item.privilegeStatus === 'INACTIVE'"
            class="badge badge-pill badge-soft-danger font-size-12"
            >{{ data.item.privilegeStatus }}</span
          >
          <span
            v-if="data.item.privilegeStatus === 'PENDING'"
            class="badge badge-pill badge-soft-warning font-size-12"
            >{{ data.item.privilegeStatus }}</span
          >
        </p>
      </template>

      <!-- Optional default data cell scoped slot -->
      <template v-slot:cell()="data">
        <i>{{ data.value }}</i>
      </template>
    </b-table>

    <b-row>
      <b-col sm="5" md="4" class="my-1">
        <b-form-group
          label="Per page"
          label-cols-sm="6"
          label-cols-md="4"
          label-cols-lg="3"
          label-align-sm="right"
          label-size="sm"
          label-for="perPageSelect"
          class="mb-0"
        >
          <b-form-select
            v-model="perPage"
            id="perPageSelect"
            size="sm"
            :options="pageOptions"
          ></b-form-select>
        </b-form-group>
      </b-col>

      <b-col sm="7" md="8" class="my-1">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="fill"
          size="sm"
          class="my-0"
        ></b-pagination>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "AllPrivilegeTable",

  data() {
    return {
      sortBy: "date",
      sortDesc: false,
      totalRows: 1,
      currentPage: 1,
      perPage: 5,
      pageOptions: [5, 10, 15],
      sortDirection: "asc",
      filter: null,
      filterOn: [],
      // Note 'isActive' is left out and will not appear in the rendered table
      fields: [
        // A virtual column that doesn't exist in items
        { key: "index", label: "S/N", sortable: true, class: "text-center" },

        // A column that needs custom formatting
        { key: "privilegeId", label: "Id", sortable: true },
        {
          key: "privilegeModuleName",
          label: "Modulename",
          sortable: true
        },
        { key: "privilegeMaker", label: "Maker", sortable: true },
        { key: "privilegeType", label: "Type", sortable: true },
        // { key: "privilegeChecker", label: "Checker", sortable: true },
        { key: "privilegeStatus", label: "Status", sortable: true }
      ],

      json_fields: {
        "Privilege Id": "privilegeId",
        "Privilege Module Name": "privilegeModuleName",
        "Privilege Module Id": "privilegeModuleId",
        "Privilege Type": "privilegeType",
        "Privilege Maker": "privilegeMaker",
        "Privilege Checker": "privilegeChecker",
        "Privilege Status": "privilegeStatus"
      },
      json_meta: [
        [
          {
            key: "charset",
            value: "utf-8"
          }
        ]
      ]
    };
  },
  computed: {
    ...mapState({
      items: state => state.privilege.privileges,
      isBusy: state => state.privilege.moduleLoadingThree
    }),
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => {
          return { text: f.label, value: f.key };
        });
    }
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.items.length;
    this.filter = " ";
  },
  methods: {
    returnItems() {
      // eslint-disable-next-line no-console
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    }
  },

  created() {
    const payload = {
      privilegeId: this.$store.getters["user/getUserId"],
      readAll: "YES"
    };

    this.$store.dispatch("privilege/allPrivileges", payload);
  }
};
</script>

<style scoped>
/*.VueTables__date-filter {*/
/*  border: 1px solid #ccc;*/
/*  padding: 6px;*/
/*  border-radius: 4px;*/
/*  cursor: pointer;*/
/*}*/
</style>
